/**
 * Account customization applied to the running shell (M8-P2 §6.12).
 *
 * `shell.bootstrap` carries an `account` block; this hook writes it onto `:root` as CSS custom
 * properties so the top bar and every primary button repaint **without a reload** when an
 * admin changes the brand colour (P2 acceptance D1). It is additive: nothing in F4's shell
 * changes, and a page that never calls it keeps F7's compiled defaults.
 */
import { useEffect } from 'react';

export interface AccountBranding {
  productName?: string | undefined;
  wordmark?: string | null | undefined;
  logoUrl?: string | null | undefined;
  /**
   * Optional on purpose: a `shell.bootstrap` answered by an older api build, by the degraded
   * path, or by a test fixture carries no `brand`, and the shell must still render with F7's
   * compiled defaults rather than throwing inside a hook.
   */
  brand?: { primary?: string; hover?: string; onPrimary?: string } | undefined;
}

type BrandKey = 'primary' | 'hover' | 'onPrimary';

/** The three properties F7's tokens read for the primary action colour. */
const VARS: Array<[BrandKey, string]> = [
  ['primary', '--ez-color-primary'],
  ['hover', '--ez-color-primary-hover'],
  ['onPrimary', '--ez-color-on-primary'],
];

export function applyAccountBranding(branding: AccountBranding | undefined | null): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const brand = branding?.brand;
  if (!brand) {
    for (const [, name] of VARS) root.style.removeProperty(name);
    if (branding?.productName) root.dataset['ezProduct'] = branding.productName;
    return;
  }
  for (const [key, name] of VARS) {
    const value = brand[key];
    if (typeof value === 'string' && value.length > 0) root.style.setProperty(name, value);
    else root.style.removeProperty(name);
  }
  if (branding?.productName) root.dataset['ezProduct'] = branding.productName;
}

export function useAccountBranding(branding: AccountBranding | undefined | null): void {
  useEffect(() => {
    applyAccountBranding(branding);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branding?.brand?.primary, branding?.brand?.hover, branding?.brand?.onPrimary, branding?.productName]);
}
